/**
 * Headline Formatter
 *
 * Template-based headline system that allows per-client configuration
 * using variable interpolation from calculation results.
 *
 * =============================================================================
 * TEMPLATE SYNTAX (Handlebars-style)
 * =============================================================================
 *
 * 1. SIMPLE VARIABLE INTERPOLATION
 *    {{variableName}}
 *    Example: "Your Portfolio: {{totalValue}}" → "Your Portfolio: $5,725,388"
 *
 * 2. EXISTENCE CHECK - Show content only if variable exists and is non-zero
 *    {{#if variableName}}content with {{nestedVar}}{{/if}}
 *    Example: "{{#if tvpi}}({{tvpi}} TVPI){{/if}}" → "(1.91x TVPI)" or "" if tvpi is 0
 *
 * 3. THRESHOLD CONDITIONAL - Compare variable to a number
 *    {{#if variableName > threshold}}trueValue{{else}}falseValue{{/if}}
 *    Operators: >, >=, <, <=, ==, !=
 *    Example: "{{#if tvpi > 1}}Profitable{{else}}In Progress{{/if}}"
 *
 * 4. DEFAULT VALUE - Show default if variable is missing/empty
 *    {{variableName | default: "fallbackValue"}}
 *    Example: "{{labelPaidIn | default: \"Paid-In Capital\"}}" → custom label or default
 *
 * 5. NUMBER FORMATTING - Control decimal places for currency values
 *    {{variableName | dollars}}  - Rounded to whole dollars (default for currency)
 *    {{variableName | cents}}    - Show dollars and cents (2 decimal places)
 *    {{variableName | round}}    - Round to integer (percentages: 19.63%% -> 20%%, multipliers: 1.91x -> 2x)
 *    Example: "{{totalValue | cents}}" → "$1,234.56"
 *    Example: "{{totalValue | dollars}}" → "$1,234"
 *
 * 6. MATH EXPRESSIONS - Simple arithmetic with variables (left-to-right evaluation)
 *    {{var1 + var2}}            - Addition
 *    {{var1 - var2}}            - Subtraction
 *    {{var1 * var2}}            - Multiplication
 *    {{var1 / var2}}            - Division
 *    {{var1 - var2 - var3}}     - Chained operations (left-to-right)
 *
 *    Examples:
 *    "{{eligible - owned}}" → "7" (if eligible=10, owned=3)
 *    "{{eligible - owned - committed}}" → "0" (if eligible=10, owned=3, committed=7)
 *    "{{owned + committed}}" → "10" (if owned=3, committed=7)
 *
 *    Note: Evaluates left-to-right, no operator precedence.
 *    To calculate a - (b + c), rewrite as: a - b - c
 *
 * =============================================================================
 * SECURITY
 * =============================================================================
 * - No eval() or Function() - all parsing is manual regex + switch
 * - Whitelist-based operators - only defined operators work
 * - HTML escaping on all raw string outputs - prevents XSS
 * - Prototype pollution protection - reserved names blocked
 * - Template length limit - prevents DoS
 * =============================================================================
 *
 * =============================================================================
 * LEGACY CLIENT TEMPLATE MIGRATIONS
 * =============================================================================
 * Below are the template equivalents for clients with hardcoded headlines.
 * To migrate a client, add these to clientData.language in the database.
 *
 * -----------------------------------------------------------------------------
 * DEFAULT (most clients without custom logic)
 * -----------------------------------------------------------------------------
 * // Portfolio view - shows paid-in capital
 * headline_template: "Your Total {{labelPaidIn}}: {{totalPaidInCapital}}"
 *
 * // Class view - shows class value
 * class_headline_template: "{{className}}: {{classValue}}"
 *
 * -----------------------------------------------------------------------------
 * AES (AE Studio) - Shows net value (current - paid in)
 * -----------------------------------------------------------------------------
 * headline_template: "Total Value of Your Portfolio: {{netValue}}"
 *
 * -----------------------------------------------------------------------------
 * ACR (Acropolis) - Shows PIC with cash balance in subtitle
 * -----------------------------------------------------------------------------
 * headline_template: "Your Total {{labelPaidIn}}: {{totalPaidInCapital}}"
 * headline_subtitle_template: "{{totalCash}} in cash"
 *
 * -----------------------------------------------------------------------------
 * PHJ (PharmaJet) - Shows rounded PIC
 * -----------------------------------------------------------------------------
 * headline_template: "Your Total {{labelPaidIn}}: {{totalPaidInCapital}}"
 *
 * -----------------------------------------------------------------------------
 * FAV (FAV Funds) - Calculator-based with TVPI
 * -----------------------------------------------------------------------------
 * headline_template: "Your Portfolio Value: {{totalValue}}"
 * headline_subtitle_template: "{{tvpi}} TVPI"
 * class_headline_template: "{{className}}: {{classValue}}"
 * class_subtitle_template: "{{classTvpi}} TVPI"
 *
 * -----------------------------------------------------------------------------
 * NTH, PSS - Shows class value with account type and valuation date
 * -----------------------------------------------------------------------------
 * class_headline_template: "Value of your {{symbol}} {{accountType}}: {{classValue}}"
 * class_subtitle_template: "{{#if currentClassValuationDate}}(as of {{currentClassValuationDate}}){{/if}}"
 *
 * -----------------------------------------------------------------------------
 * ORR (Acropolis Aggregate) - Shows current value
 * -----------------------------------------------------------------------------
 * headline_template: "Current Value: {{totalCurrentValue}}"
 * class_headline_template: "Current Value of your {{symbol}}: {{classValue}}"
 *
 * -----------------------------------------------------------------------------
 * SHOW_RETURNS clients (distributions-focused)
 * -----------------------------------------------------------------------------
 * headline_template: "Your Total {{labelPaidIn}}: {{totalDistributions}}"
 *
 * -----------------------------------------------------------------------------
 * Positive ROI clients (value > paid in)
 * -----------------------------------------------------------------------------
 * headline_template: "Aggregate Value + Return: {{totalCurrentValue}}"
 *
 * -----------------------------------------------------------------------------
 * BW (Barry Wehmiller) - Vested/unvested values with tertiary line
 * -----------------------------------------------------------------------------
 * headline_template: "Your Current {{#if totalForecastedValue}}Vested {{/if}}Value as of {{valuationDate}}: {{vestedValue}}"
 * headline_subtitle_template: "{{#if totalForecastedValue}}Forecasted Unvested Grant Value: {{totalForecastedValue}}{{/if}}"
 * headline_tertiary_template: "{{#if totalOriginalGrantValue}}Original Grant Value: {{totalOriginalGrantValue}}{{/if}}"
 * class_headline_template: "Your Current {{#if classUnvestedValue}}Vested {{/if}}Value: {{classVestedValue}}"
 * class_subtitle_template: "{{#if classUnvestedValue}}Unvested Grant Value: {{classUnvestedValue}}{{/if}}"
 * class_tertiary_template: "Total: {{classValue}}"
 *
 * =============================================================================
 * TEMPLATE KEYS (in clientData.language)
 * =============================================================================
 * Portfolio-level:
 *   headline_template           - Main headline (line 1)
 *   headline_subtitle_template  - Subtitle (line 2)
 *   headline_tertiary_template  - Tertiary line (line 3)
 *
 * Class-level:
 *   class_headline_template     - Main headline for class view
 *   class_subtitle_template     - Subtitle for class view
 *   class_tertiary_template     - Tertiary line for class view
 *
 * =============================================================================
 * AVAILABLE TEMPLATE VARIABLES
 * =============================================================================
 *
 * All currency variables support format modifiers:
 *   {{totalValue}}           - Rounded dollars (default): $1,234
 *   {{totalValue | dollars}} - Explicit rounded dollars: $1,234
 *   {{totalValue | cents}}   - Dollars and cents: $1,234.56
 *
 * PORTFOLIO VIEW (from legacyData or calculatorResult):
 * - {{totalPaidInCapital}}  - Sum of cost basis across all classes
 * - {{totalCurrentValue}}   - Sum of current values across all classes
 * - {{totalValue}}          - Alias for totalCurrentValue (or calculator total)
 * - {{totalDistributions}}  - Sum of distributions received
 * - {{totalContributions}}  - Alias for totalPaidInCapital
 * - {{netValue}}            - totalCurrentValue - totalPaidInCapital
 * - {{totalCash}}           - Sum of CASH class values (for ACR)
 * - {{pic}}                 - Alias for totalPaidInCapital
 * - {{capitalAccount}}      - Alias for totalPaidInCapital
 * - {{labelPaidIn}}         - Custom label (e.g., "Paid-In Capital", "Investment")
 * - {{accountType}}         - "account" (funds) or "shares" (companies)
 * - {{valuationDate}}       - Most recent valuation date (e.g., "Dec 13, 2024")
 *
 * PORTFOLIO VIEW (calculator-only, when available):
 * - {{tvpi}}                - Total Value to Paid-In multiple (e.g., 1.25x)
 * - {{dpi}}                 - Distributions to Paid-In multiple
 * - {{rvpi}}                - Residual Value to Paid-In multiple
 * - {{moic}}                - Multiple on Invested Capital
 * - {{vestedValue}}         - Value of vested shares (all classes)
 * - {{unvestedValue}}       - Value of unvested shares (all classes)
 * - {{totalOwned}}          - Sum of owned shares across classes (raw from userData)
 * - {{totalOwnedValue}}     - Sum of owned × per-share value across classes
 * - {{totalApproved}}       - Sum of approved shares across classes (raw from userData)
 * - {{totalApprovedValue}}  - Sum of approved × per-share value across classes
 * - {{totalUnapproved}}     - Sum of (owned - approved) across classes
 * - {{totalUnapprovedValue}} - Sum of unapproved × per-share value across classes
 *
 * GRANT VALUES (parsed from vesting conditions - BW-style):
 * - {{totalOriginalGrantValue}} - Sum of Original Grant Value for unvested grants
 * - {{totalForecastedValue}}    - Sum of Forecasted Value for unvested grants
 * - {{totalActualGrantValue}}   - Sum of Actual Grant Value for vested grants
 *
 * CLASS VIEW (from legacyData or classData):
 * - {{className}}           - Display name of the equity class
 * - {{symbol}}              - Class key/ticker (e.g., "AC", "JC")
 * - {{classKey}}            - Alias for symbol
 * - {{classValue}}          - Current value of this class
 * - {{value}}               - Alias for classValue
 * - {{classBasis}}          - Cost basis for this class
 * - {{basis}}               - Alias for classBasis
 * - {{contributions}}       - User's contributions to this class
 * - {{distributions}}       - Distributions received from this class
 * - {{accountType}}         - "account" (funds) or "shares" (companies)
 * - {{currentClassValuationDate}} - Valuation date for this class (e.g., "12/13/2024")
 * - {{classTvpi}}           - TVPI for this specific class (calculator-only)
 * - {{ownership}}           - User's ownership percentage (calculator-only)
 * - {{fundNav}}             - Total fund NAV (calculator-only)
 * - {{classVestedValue}}    - Value of vested shares for this class
 * - {{classUnvestedValue}}  - Value of unvested shares for this class
 * - {{owned}}               - Raw owned share count for this class (from userData)
 * - {{classOwned}}          - Alias for owned
 * - {{ownedValue}}          - owned × per-share value for this class
 * - {{classOwnedValue}}     - Alias for ownedValue
 * - {{approved}}            - Raw approved share count for this class (from userData)
 * - {{classApproved}}       - Alias for approved
 * - {{approvedValue}}       - approved × per-share value for this class
 * - {{classApprovedValue}}  - Alias for approvedValue
 * - {{unapproved}}          - owned - approved for this class
 * - {{classUnapproved}}     - Alias for unapproved
 * - {{unapprovedValue}}     - unapproved × per-share value for this class
 * - {{classUnapprovedValue}} - Alias for unapprovedValue
 * - {{totalPaidInCapital}}  - Aggregate paid-in capital across all classes (portfolio total)
 *
 * STOCK STATUS (Fishbeck-style eligibility, only present if stockStatus provided):
 * - {{stockStatus}}         - User's stock status (e.g., "10", "100", "Associate")
 * - {{eligible}}            - Maximum shares user can own (derived from stockStatus if numeric,
 *                             or owned for text status like "Associate", "Senior Associate")
 *
 * =============================================================================
 */

import { TemplateUtils } from '../helpers/TemplateUtils.js';
import { VariableProvider } from '../providers/VariableProvider.js';
import { resolveColumnValue, mapCustomColumnFormatToType } from '../helpers/field-engine/ColumnResolvers.js';
import { getCAGR } from '../helpers/cagr-utils.js';
import { resolveClassTotalShares } from '../helpers/class-aggregates.js';

const HeadlineFormatter = {
	/**
	 * Variable type definitions for auto-formatting
	 */
	variableTypes: {
		// Currency variables - fund metrics
		totalValue: 'currency',
		totalContributions: 'currency',
		totalDistributions: 'currency',
		totalPendingDistributions: 'currency', // #5242: future-dated distributions, portfolio-wide
		totalCapitalAccount: 'currency',
		capitalAccount: 'currency',
		pic: 'currency',

		// Currency variables - income allocations (tax basis + market value)
		totalTaxBasis: 'currency',
		taxBasis: 'currency',
		totalIncomeAllocations: 'currency',
		incomeAllocations: 'currency',
		classIncomeAllocations: 'currency',
		classTaxBasis: 'currency',
		totalMarketValue: 'currency',
		marketValue: 'currency',
		classMarketValue: 'currency',
		totalUnrealizedGain: 'currency',
		unrealizedGain: 'currency',
		classUnrealizedGain: 'currency',

		// Currency variables - private company metrics
		totalPaidInCapital: 'currency',
		uninvestedCapital: 'currency',
		vestedValue: 'currency',
		unvestedValue: 'currency', // Unvested grant value (totalValue - vestedValue)
		classVestedValue: 'currency', // Per-class vested value
		classUnvestedValue: 'currency', // Per-class unvested value
		currentValue: 'currency',
		netValue: 'currency',
		totalCurrentValue: 'currency',

		// Owned/Approved/Unapproved metrics (raw values from userData, LTIPR source of truth)
		totalOwned: 'number', // Portfolio-level: sum of owned across classes
		totalOwnedValue: 'currency', // Portfolio-level: sum of owned × per-share value
		totalApproved: 'number', // Portfolio-level: sum of approved across classes
		totalApprovedValue: 'currency', // Portfolio-level: sum of approved × per-share value
		totalUnapproved: 'number', // Portfolio-level: sum of (owned - approved)
		totalUnapprovedValue: 'currency', // Portfolio-level: sum of unapproved × per-share value
		owned: 'number', // Class-level: raw owned share count
		classOwned: 'number', // Alias for owned
		ownedFraction: 'number', // Class-level: owned / 100 (decimal form for percentage-based ownership)
		ownershipDecimal: 'number', // Class-level: owned / totalClassShares (fractional ownership, e.g. 0.0025 for 0.25%)
		ownedValue: 'currency', // Class-level: owned × per-share value
		classOwnedValue: 'currency', // Alias for ownedValue
		approved: 'number', // Class-level: raw approved share count
		classApproved: 'number', // Alias for approved
		approvedValue: 'currency', // Class-level: approved × per-share value
		classApprovedValue: 'currency', // Alias for approvedValue
		unapproved: 'number', // Class-level: owned - approved
		classUnapproved: 'number', // Alias for unapproved
		unapprovedValue: 'currency', // Class-level: unapproved × per-share value
		classUnapprovedValue: 'currency', // Alias for unapprovedValue

		// 24X projection (Cerity-specific; sourced from userData.clients[client].value24x)
		value24x: 'currency',
		cagr24x: 'percentage', // CAGR from user's first→latest event projected to value24x

		// Grant value metrics (parsed from vesting conditions - BW-style)
		totalOriginalGrantValue: 'currency', // Sum of Original Grant Value for unvested grants
		totalForecastedValue: 'currency', // Sum of Forecasted Value for unvested grants
		totalActualGrantValue: 'currency', // Sum of Actual Grant Value for vested grants
		classOriginalGrantValue: 'currency', // Per-class Original Grant Value for unvested grants
		classForecastedValue: 'currency', // Per-class Forecasted Value for unvested grants
		classActualGrantValue: 'currency', // Per-class Actual Grant Value for vested grants

		// Currency variables - class-specific metrics
		classValue: 'currency',
		classTotalValue: 'currency',
		classContributions: 'currency',
		classDistributions: 'currency',
		classPendingDistributions: 'currency', // #5242: future-dated distributions, per class
		classReturnsOfCapital: 'currency',
		fundNav: 'currency',
		value: 'currency',
		contributions: 'currency',
		distributions: 'currency',
		pendingDistributions: 'currency', // #5242: future-dated distributions, per class
		returnsOfCapital: 'currency',
		sharePrice: 'currency',
		classSharePrice: 'currency',

		// Multiplier variables (fund metrics)
		tvpi: 'multiplier',
		dpi: 'multiplier',
		rvpi: 'multiplier',
		moic: 'multiplier',
		classTvpi: 'multiplier',

		// Growth metrics
		cagr: 'percentage',

		// Percentage variables
		userPercentage: 'ownershipPercentage',
		ownership: 'ownershipPercentage',
		priceChange: 'number', // Price change % from prior valuation (signed, for direction conditionals)
		classPriceChange: 'number', // Per-class price change % (signed)
		absPriceChange: 'number', // Absolute price change % (for display)
		classAbsPriceChange: 'number', // Per-class absolute price change %

		// Legacy variables - for non-calculator clients
		totalCash: 'currency', // Sum of CASH classes (e.g., ACR)
		basis: 'currency', // Per-class cost basis
		classBasis: 'currency', // Alias for basis

		// Text/label variables
		labelPaidIn: 'raw', // Custom label (e.g., "Paid-In Capital")
		accountType: 'raw', // "account" or "shares"
		currentLabel: 'raw', // Per-class headline label from equity config
		classKey: 'raw',
		className: 'raw',
		symbol: 'raw',

		// Date variables
		valuationDate: 'raw', // Most recent valuation date across all classes (e.g., "Dec 13, 2024")
		currentClassValuationDate: 'raw', // Valuation date for current class (e.g., "12/13/2024")

		// Stock status / eligibility (Fishbeck-style)
		stockStatus: 'raw', // User's stock status (e.g., "10", "100", "Associate")
		eligible: 'number', // Maximum shares user can own
	},

	// ========================================
	// CUSTOM CAP TABLE COLUMN RESOLUTION
	// ========================================

	/**
	 * Check if a custom column source is manual (value stored directly on userClient)
	 * @param {string} source - Column source definition
	 * @returns {boolean}
	 */
	_isManualColumnSource: function (source) {
		const s = (source || '').trim();
		if (!s) return true;
		if (/^formula:/i.test(s) || /^aggregate:/i.test(s) || /^calculator:/i.test(s)) return false;
		if (s.includes(':')) return false;
		if (s.startsWith('userClient.') || s.startsWith('user.')) return false;
		if (s.includes('.')) return false;
		return true;
	},

	/**
	 * Read a manual column value from userClient with legacy key fallback
	 * @param {Object} userClient - The user's client record
	 * @param {string} key - Column key
	 * @param {string} [label] - Column label (for legacy camelCase fallback)
	 * @returns {*} - Stored value or empty string
	 */
	_readManualColumnValue: function (userClient, key, label) {
		if (!userClient || !key) return '';
		// Canonical: userClient[key]
		let value = userClient[key];
		if (value !== undefined && value !== null && String(value).trim() !== '') return value;
		// Legacy: userClient['ctcc_' + key]
		value = userClient['ctcc_' + key];
		if (value !== undefined && value !== null && String(value).trim() !== '') return value;
		// Legacy camelCase(label) fallback
		if (label) {
			const parts = String(label)
				.trim()
				.split(/[^a-zA-Z0-9]+/)
				.filter(Boolean);
			if (parts.length) {
				const camel =
					parts[0].toLowerCase() +
					parts
						.slice(1)
						.map(function (w) {
							return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
						})
						.join('');
				if (camel !== key) {
					value = userClient[camel];
					if (value !== undefined && value !== null && String(value).trim() !== '') return value;
				}
			}
		}
		return '';
	},

	/**
	 * Resolve all custom cap table column values and inject into vars/formulas
	 * @param {Object} vars - Variable map to populate
	 * @param {Object} dynamicTypes - Per-call type overrides (avoids mutating shared variableTypes)
	 * @param {Object} formulas - Formula descriptions map to populate
	 * @param {Object} options - Data context
	 * @param {Object} options.userData - Full user data object
	 * @param {Object} options.clientData - Full client data object
	 * @param {string} options.client - Client code
	 */
	_resolveCustomColumns: function (vars, dynamicTypes, formulas, options) {
		const { userData, clientData, client } = options;
		if (!clientData) return;

		const columns = clientData.cap_table_custom_columns;
		if (!Array.isArray(columns)) return;

		const userClient = userData?.clients?.[client];
		if (!userClient) return;

		const self = this;
		const shareClasses = Object.keys(clientData.equity || {});

		// Build context for resolveColumnValue (non-manual columns)
		let resolverContext = null;
		const getResolverContext = function () {
			if (resolverContext) return resolverContext;
			let totalOwned = 0;
			const userEvents = [];
			shareClasses.forEach(function (sc) {
				const uc = userClient.shares?.[sc];
				if (uc) {
					totalOwned += Number(uc.owned) || 0;
					if (uc.events) userEvents.push.apply(userEvents, uc.events);
				}
			});
			const clientEvents = [];
			shareClasses.forEach(function (sc) {
				const ce = clientData.equity?.[sc]?.events || [];
				clientEvents.push.apply(clientEvents, ce);
			});
			resolverContext = {
				user: userData,
				userClient: userClient,
				userid: userData.userid,
				shares: { owned: totalOwned },
				owned: totalOwned,
				totalShares: 0, // Not available in headline context (no full cap table)
				userEvents: userEvents,
				clientEvents: clientEvents,
				clientData: clientData,
				shareClasses: shareClasses,
			};
			return resolverContext;
		};

		columns.forEach(function (col) {
			if (!col || !col.key) return;
			// Skip columns whose key collides with a built-in variable
			if (vars[col.key] !== undefined) return;

			let value;
			const isManual = self._isManualColumnSource(col.source);

			if (isManual) {
				value = self._readManualColumnValue(userClient, col.key, col.label);
			} else {
				try {
					value = resolveColumnValue({ key: col.key, source: col.source }, getResolverContext(), {});
				} catch (e) {
					// Resolver errors shouldn't break headline rendering
					console.warn('[HeadlineFormatter] Error resolving custom column', col.key, e);
					return;
				}
			}

			// Skip empty values — treats '', null, undefined uniformly
			if (value === undefined || value === null || value === '') return;

			// Coerce numeric strings for typed columns (strip commas from imported values)
			const type = mapCustomColumnFormatToType(col.format);
			if (type !== 'raw' && typeof value === 'string') {
				const num = Number(value.replace(/,/g, ''));
				if (!isNaN(num)) value = num;
			}

			vars[col.key] = value;
			// Register type in per-call map (avoids mutating shared variableTypes)
			dynamicTypes[col.key] = type;
			// Add formula description
			if (formulas) {
				formulas[col.key] = isManual ? 'Custom column: ' + (col.label || col.key) : 'Custom column (' + (col.source || '') + ')';
			}
		});
	},

	// ========================================
	// SECURITY UTILITIES (delegated to TemplateUtils)
	// ========================================

	/**
	 * Safely get a variable value with prototype pollution protection
	 * Delegates to TemplateUtils.safeGetVar
	 */
	safeGetVar: function (vars, varName) {
		return TemplateUtils.safeGetVar(vars, varName);
	},

	/**
	 * Escape HTML entities to prevent XSS attacks
	 * Delegates to TemplateUtils.escapeHtml
	 */
	escapeHtml: function (str) {
		return TemplateUtils.escapeHtml(str);
	},

	// ========================================
	// CONDITIONAL PROCESSING (delegated to TemplateUtils)
	// ========================================

	/**
	 * Evaluate a comparison between a value and threshold
	 * Delegates to TemplateUtils.evaluateComparison
	 */
	evaluateComparison: function (value, operator, threshold) {
		return TemplateUtils.evaluateComparison(value, operator, threshold);
	},

	/**
	 * Check if a value is "truthy" for template conditionals
	 * Delegates to TemplateUtils.isTruthy
	 */
	isTruthy: function (value) {
		return TemplateUtils.isTruthy(value);
	},

	/**
	 * Process {{#if ...}}...{{else}}...{{/if}} blocks
	 * Delegates to TemplateUtils.processIfBlocks
	 */
	processIfBlocks: function (template, vars) {
		return TemplateUtils.processIfBlocks(template, vars);
	},

	/**
	 * Process {{var | default: "value"}} expressions
	 * @param {string} template - Template string
	 * @param {Object} vars - Variable map
	 * @returns {string} - Template with defaults resolved
	 */
	processDefaults: function (template, vars) {
		const self = this;

		// Match {{varName | default: "value"}} or {{varName | default: value}}
		// Also supports path vars: {{user.field | default: "value"}}
		const defaultRegex = /\{\{([\w.]+)\s*\|\s*default:\s*"?([^"}]+)"?\}\}/g;

		return template.replace(defaultRegex, function (match, varExpr, defaultVal) {
			let value;
			// Check if it's a path variable
			if (varExpr.includes('.') && /^(user|shares|config|aggregate)\./.test(varExpr)) {
				const context = vars._context || {};
				value = self.resolvePathVariable(varExpr, context);
			} else {
				value = self.safeGetVar(vars, varExpr);
			}
			if (self.isTruthy(value)) {
				const type = self.variableTypes[varExpr] || 'raw';
				return self.formatValue(value, type);
			}
			return self.escapeHtml(defaultVal.trim());
		});
	},

	/**
	 * Resolve a path-based variable like "user.idNumber" or "shares.eligible"
	 * @param {string} path - Dot-notation path with prefix (user., shares., config., aggregate.)
	 * @param {Object} context - Context object with data sources
	 * @returns {*} - Resolved value or undefined
	 */
	resolvePathVariable: function (path, context) {
		if (!path || !context) return undefined;

		const parts = path.split('.');
		if (parts.length < 2) return undefined;

		const prefix = parts[0];
		const remainingPath = parts.slice(1).join('.');

		const source = context[prefix];
		if (!source) return undefined;

		return VariableProvider.resolvePath(source, remainingPath, {});
	},

	/**
	 * Process all conditional expressions in a template
	 * Supports: {{#if var}}...{{/if}}, {{#if var > N}}...{{else}}...{{/if}}, {{var | default: "val"}}
	 * @param {string} template - Template with conditional expressions
	 * @param {Object} vars - Variable map
	 * @returns {string} - Template with conditionals resolved
	 */
	processConditionals: function (template, vars) {
		if (!template || typeof template !== 'string') return '';

		// Enforce template length limit for DoS prevention
		if (template.length > TemplateUtils.MAX_TEMPLATE_LENGTH) {
			console.warn('[HeadlineFormatter] Template exceeds max length, truncating');
			template = template.substring(0, TemplateUtils.MAX_TEMPLATE_LENGTH);
		}

		// Process if blocks first (may be nested, so process multiple times)
		let previousTemplate;
		let iterations = 0;
		const maxIterations = 10; // Prevent infinite loops

		do {
			previousTemplate = template;
			template = this.processIfBlocks(template, vars);
			iterations++;
		} while (template !== previousTemplate && iterations < maxIterations);

		// Process defaults (formatter-specific, uses variableTypes)
		template = this.processDefaults(template, vars);

		return template;
	},

	/**
	 * Process math expressions in templates
	 * Delegates to TemplateUtils.processMathExpressions
	 */
	processMathExpressions: function (template, vars) {
		return TemplateUtils.processMathExpressions(template, vars);
	},

	/**
	 * Process {{#sum}}...{{/sum}} blocks. See buildUnifiedVariableMap's
	 * _classVarMaps comment for what populates the per-class data this reads.
	 *
	 * Runs before every other template pass (conditionals, math expressions,
	 * interpolation): the block's own inner {{...}} refers to class-level
	 * variables (e.g. ownershipDecimal, classSharePrice) that don't exist in
	 * the portfolio `vars` map those later passes operate on.
	 *
	 * vars._classVarMaps being undefined (no {{#sum}} in this render's own
	 * templates, or a render path that never builds it, e.g. class-view)
	 * leaves the block as raw markup -- a misconfigured template stays visibly
	 * wrong rather than silently showing 0. An empty array (this render DOES
	 * support {{#sum}}, but the shareholder owns no classes) resolves to 0.
	 *
	 * @param {string} template
	 * @param {Object} vars - Portfolio vars from buildUnifiedVariableMap
	 * @returns {string}
	 */
	processSumBlocks: function (template, vars) {
		if (!template || typeof template !== 'string' || !template.includes('{{#sum}}')) return template;
		if (!Array.isArray(vars?._classVarMaps)) return template;

		const classVarMaps = vars._classVarMaps;

		return template.replace(/\{\{#sum\}\}([\s\S]*?)\{\{\/sum\}\}/g, function (_match, inner) {
			let sum = 0;
			classVarMaps.forEach(function (classVars) {
				sum += TemplateUtils.sumMathExpressions(inner, classVars);
			});
			return TemplateUtils.formatMathResult(sum);
		});
	},

	/**
	 * Get numeric value from vars
	 * Delegates to TemplateUtils.getNumericValue
	 */
	getNumericValue: function (vars, varName) {
		return TemplateUtils.getNumericValue(vars, varName);
	},

	/**
	 * Format a single value based on its type
	 * Raw strings are HTML-escaped for XSS prevention
	 * @param {*} value - The value to format
	 * @param {string} type - 'currency', 'multiplier', 'percentage', or 'raw'
	 * @param {string} [formatOverride] - Optional format override ('dollars', 'cents')
	 * @returns {string} - Formatted string (HTML-safe)
	 */
	formatValue: function (value, type, formatOverride) {
		if (value === undefined || value === null) return '';

		switch (type) {
			case 'currency':
				if (typeof value !== 'number' || isNaN(value)) return '';
				// Check for format override
				if (formatOverride === 'cents') {
					return (
						'$' +
						value.toLocaleString('en-US', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})
					);
				}
				// Default: rounded dollars (also handles explicit 'dollars' override)
				return (
					'$' +
					value.toLocaleString('en-US', {
						minimumFractionDigits: 0,
						maximumFractionDigits: 0,
					})
				);

			case 'multiplier':
				if (typeof value !== 'number' || isNaN(value)) return '';
				return value.toFixed(formatOverride === 'round' ? 0 : 2) + 'x';

			case 'percentage':
				if (typeof value !== 'number' || isNaN(value)) return '';
				return (value * 100).toFixed(formatOverride === 'round' ? 0 : 2) + '%';

			case 'ownershipPercentage': {
				if (typeof value !== 'number' || isNaN(value)) return '';
				const ownershipDecimals = typeof this.ownershipPercentageDecimals === 'number' ? this.ownershipPercentageDecimals : 2;
				return (value * 100).toFixed(formatOverride === 'round' ? 0 : ownershipDecimals) + '%';
			}

			case 'number':
				if (typeof value !== 'number' || isNaN(value)) return '';
				return value.toLocaleString('en-US', this.numberFormatOptions || undefined);

			default:
				// Escape HTML for raw string values to prevent XSS
				return this.escapeHtml(String(value));
		}
	},

	// Client-specific number formatting options (e.g., BW uses 4 decimal places)
	// Set by caller before rendering. null = default JS toLocaleString behavior.
	numberFormatOptions: null,

	// Client-configurable ownership percentage decimal precision (R1/R2, #5967).
	// Set by caller before rendering (clientData.decimalsConfig.ownershipPercentage).
	// undefined/null = default of 2 decimals. Independent of cagr/cagr24x ('percentage' fields).
	ownershipPercentageDecimals: null,

	// ========================================
	// DEFAULT TEMPLATES (Handlebars-style)
	// Used when no custom template is configured
	// ========================================

	defaultTemplates: {
		// Portfolio view defaults
		portfolio: 'Your Total {{labelPaidIn}}: {{totalPaidInCapital}}',
		portfolioSubtitle: '',
		portfolioTertiary: '',

		// Class view defaults
		class: '{{className}}: {{classValue}}',
		classSubtitle: '',
		classTertiary: '',
	},

	/**
	 * Get template with fallback to default
	 * @param {string} customTemplate - Custom template from config (may be undefined)
	 * @param {string} defaultKey - Key for default template ('portfolio', 'class', etc.)
	 * @returns {string} - Template to use
	 */
	getTemplate: function (customTemplate, defaultKey) {
		return customTemplate || this.defaultTemplates[defaultKey] || '';
	},

	/**
	 * Build a unified variable map from available data sources
	 * Merges calculator results (if available) with legacy variables
	 * @param {Object} options
	 * @param {Object} options.calculatorResult - Result from calculator (optional)
	 * @param {Object} options.legacyData - Legacy values from getEquityData()
	 * @returns {Object} - Flat map of variable names to values
	 */
	buildUnifiedVariableMap: function (options = {}) {
		const { calculatorResult, legacyData = {}, classAggregates, needsClassVarMaps = true } = options;

		const {
			totalPaidInCapital = 0,
			totalCurrentValue = 0,
			totalDistributions = 0,
			totalPendingDistributions = 0,
			netValue = 0,
			latestValue = {},
			shareKeys = [],
			labelPaidIn = 'Paid-In Capital',
			itsaFund = false,
			valuationDate = '',
		} = legacyData;

		// Calculate total cash (sum of CASH classes, used by ACR)
		let totalCash = 0;
		shareKeys.forEach(function (key) {
			if (key === 'CASH' || key.includes('CASH')) {
				totalCash += latestValue[key] || 0;
			}
		});

		// Start with legacy values (always available)
		const vars = {
			// Primary values
			totalPaidInCapital: totalPaidInCapital,
			totalCurrentValue: totalCurrentValue,
			totalDistributions: totalDistributions,
			totalPendingDistributions: totalPendingDistributions,
			netValue: netValue,

			// Aliases for template convenience
			totalValue: totalCurrentValue,
			totalContributions: totalPaidInCapital,
			pic: totalPaidInCapital,
			capitalAccount: totalPaidInCapital,

			// Special values
			totalCash: totalCash,

			// Labels and metadata
			labelPaidIn: labelPaidIn,
			accountType: itsaFund ? 'account' : 'shares',
			valuationDate: valuationDate,
		};

		// 24X projection (Cerity): per-shareholder dollar value at 24× multiple,
		// stored as a string with commas at userData.clients[client].value24x.
		// Consumed by Cerity's clientData.about template via {{value24x}}.
		// Independent of calculatorResult — populated whenever userData has the field.
		if (legacyData.userData && legacyData.client) {
			const value24xRaw = legacyData.userData.clients?.[legacyData.client]?.value24x;
			if (value24xRaw) {
				const parsed = parseFloat(String(value24xRaw).replace(/[^.\d]/g, ''));
				if (!isNaN(parsed) && parsed > 0) vars.value24x = parsed;
			}
		}

		// cagr24x: CAGR from the user's first→latest event projected to value24x.
		// Mirrors the with-graph branch of the deleted customizeHeadline (#5198 Phase 7b-2).
		// Uses canonical event dates from clientData.equity matched by nrid; values
		// from userData (per-shareholder grossDollars).
		if (vars.value24x && legacyData.userData && legacyData.client && legacyData.clientData?.equity) {
			const userClient = legacyData.userData.clients?.[legacyData.client];
			const clientEquity = legacyData.clientData.equity;
			let firstValue = null;
			let firstDate = Infinity;
			let latestDate = 0;
			if (userClient?.shares) {
				Object.entries(userClient.shares).forEach(function (entry) {
					const cls = entry[0];
					const share = entry[1];
					const userEvents = (share && share.events) || [];
					const clientEvents = (clientEquity[cls] && clientEquity[cls].events) || [];
					userEvents.forEach(function (ue) {
						const ce = clientEvents.find(function (e) {
							return e.nrid === ue.nrid;
						});
						if (!ce || !ce.date) return;
						const d = Number(ce.date);
						const v = Number(ue.grossDollars);
						if (!d || !v) return;
						if (d < firstDate) {
							firstDate = d;
							firstValue = v;
						}
						if (d > latestDate) latestDate = d;
					});
				});
			}
			if (firstValue !== null && firstDate < latestDate) {
				const cagr = getCAGR(firstValue, vars.value24x, firstDate, latestDate);
				if (cagr !== null && Number.isFinite(cagr)) vars.cagr24x = cagr;
			}
		}

		// Overlay calculator results if available (they take precedence)
		if (calculatorResult) {
			if (calculatorResult.totalValue !== undefined) vars.totalValue = calculatorResult.totalValue;
			if (calculatorResult.totalContributions !== undefined) vars.totalContributions = calculatorResult.totalContributions;
			if (calculatorResult.totalDistributions !== undefined) vars.totalDistributions = calculatorResult.totalDistributions;
			if (calculatorResult.totalCapitalAccount !== undefined) vars.capitalAccount = calculatorResult.totalCapitalAccount;

			// Private company metrics (vested/unvested for BW-style display)
			if (calculatorResult.vestedValue !== undefined) vars.vestedValue = calculatorResult.vestedValue;
			if (calculatorResult.unvestedValue !== undefined) vars.unvestedValue = calculatorResult.unvestedValue;

			// Owned/Approved: raw values from userData (LTIPR source of truth)
			// Reads directly from user.clients[client].shares[class], no schedule derivation
			if (legacyData.userData && legacyData.client) {
				let totalOwned = 0;
				let totalOwnedValue = 0;
				let totalApproved = 0;
				let totalApprovedValue = 0;
				const userShares = legacyData.userData.clients?.[legacyData.client]?.shares;
				if (userShares && calculatorResult.breakdown) {
					calculatorResult.breakdown.forEach(function (b) {
						const perShareValue = (b.sharePrice || 0) - (b.strikePrice || 0);
						const classOwned = Number(userShares[b.classKey]?.owned || 0);
						const classApproved = Number(userShares[b.classKey]?.approved || 0);
						totalOwned += classOwned;
						totalOwnedValue += classOwned * perShareValue;
						totalApproved += classApproved;
						totalApprovedValue += classApproved * perShareValue;
					});
				}
				vars.totalOwned = totalOwned;
				vars.totalOwnedValue = totalOwnedValue;
				vars.totalApproved = totalApproved;
				vars.totalApprovedValue = totalApprovedValue;
				vars.totalUnapproved = Math.max(0, totalOwned - totalApproved);
				vars.totalUnapprovedValue = Math.max(0, totalOwnedValue - totalApprovedValue);
			}

			// Fund metrics from calculator
			if (calculatorResult.fundMetrics) {
				if (typeof calculatorResult.fundMetrics.tvpi === 'number') vars.tvpi = calculatorResult.fundMetrics.tvpi;
				if (typeof calculatorResult.fundMetrics.dpi === 'number') vars.dpi = calculatorResult.fundMetrics.dpi;
				if (typeof calculatorResult.fundMetrics.rvpi === 'number') vars.rvpi = calculatorResult.fundMetrics.rvpi;
				if (typeof calculatorResult.fundMetrics.moic === 'number') vars.moic = calculatorResult.fundMetrics.moic;
			}

			// Income allocations / valuation calculator fields
			if (calculatorResult.totalMarketValue !== undefined) vars.totalMarketValue = calculatorResult.totalMarketValue;
			if (calculatorResult.totalTaxBasis !== undefined) vars.totalTaxBasis = calculatorResult.totalTaxBasis;
			if (calculatorResult.totalIncomeAllocations !== undefined) vars.totalIncomeAllocations = calculatorResult.totalIncomeAllocations;
			if (calculatorResult.totalUnrealizedGain !== undefined) vars.totalUnrealizedGain = calculatorResult.totalUnrealizedGain;
			if (calculatorResult.totalCurrentValue !== undefined) vars.totalCurrentValue = calculatorResult.totalCurrentValue;

			// CAGR from calculator (computed post-calculation from first event to current value)
			if (calculatorResult.cagr !== undefined) vars.cagr = calculatorResult.cagr;
		}

		// Stock status eligibility calculation (Fishbeck-style)
		// Only add these variables when stockStatus is provided in legacyData
		if (legacyData.stockStatus) {
			const stockStatus = legacyData.stockStatus;
			const totalOwned = Number(legacyData.totalOwned || 0);

			vars.stockStatus = stockStatus;

			// Check if stockStatus is numeric (e.g., "10", "100")
			const numericStatus = parseInt(stockStatus, 10);
			const isNumericStatus = !isNaN(numericStatus) && String(numericStatus) === stockStatus;

			if (isNumericStatus) {
				// For numeric status: eligible = the status value (it's the cap)
				vars.eligible = numericStatus;
			} else {
				// For text status (Associate, Senior Associate, Principal, etc.):
				// eligible = owned (they own what they're eligible for)
				vars.eligible = totalOwned;
			}
		}

		// Grant values parsed from vesting conditions (BW-style)
		// These are aggregated across all unvested grants
		if (legacyData.totalOriginalGrantValue) {
			vars.totalOriginalGrantValue = legacyData.totalOriginalGrantValue;
		}
		if (legacyData.totalForecastedValue) {
			vars.totalForecastedValue = legacyData.totalForecastedValue;
		}
		if (legacyData.totalActualGrantValue) {
			vars.totalActualGrantValue = legacyData.totalActualGrantValue;
		}

		// Build formula descriptions for each variable (used by preview dropdown)
		const f$ = (v) => '$' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
		const fN = (v) => Number(v || 0).toLocaleString('en-US');
		const formulas = {};
		formulas.totalPaidInCapital = 'Sum of contributions across classes';
		formulas.totalCurrentValue = 'Sum of current values across classes';
		formulas.totalDistributions = 'Sum of distributions across classes';
		formulas.totalPendingDistributions = 'Sum of future-dated distributions across classes';
		formulas.netValue = f$(vars.totalCurrentValue) + ' - ' + f$(vars.totalPaidInCapital) + ' + ' + f$(vars.totalDistributions);
		formulas.totalValue = calculatorResult?.totalValue !== undefined ? 'Sum of class values (calculator)' : '= totalCurrentValue';
		formulas.totalContributions = calculatorResult?.totalContributions !== undefined ? 'Sum of class contributions (calculator)' : '= totalPaidInCapital';
		formulas.pic = '= totalPaidInCapital';
		formulas.capitalAccount = calculatorResult?.totalCapitalAccount !== undefined ? 'Sum of class capital accounts' : '= totalPaidInCapital';
		formulas.totalCash = 'Sum of CASH class values';
		formulas.labelPaidIn = 'Client config label';
		formulas.accountType = itsaFund ? '"account" (fund mode)' : '"shares" (equity mode)';
		formulas.valuationDate = 'Latest valuation date';
		if (vars.vestedValue !== undefined) formulas.vestedValue = 'From schedule: sum of vested × (sharePrice - strikePrice) across classes';
		if (vars.unvestedValue !== undefined) formulas.unvestedValue = 'From schedule: sum of unvested × (sharePrice - strikePrice) across classes';
		if (vars.totalOwned !== undefined) formulas.totalOwned = 'Sum of shares[class].owned';
		if (vars.totalOwnedValue !== undefined) formulas.totalOwnedValue = 'Sum of owned × (sharePrice - strikePrice) per class';
		if (vars.totalApproved !== undefined) formulas.totalApproved = 'Sum of shares[class].approved';
		if (vars.totalApprovedValue !== undefined) formulas.totalApprovedValue = 'Sum of approved × (sharePrice - strikePrice) per class';
		if (vars.totalUnapproved !== undefined)
			formulas.totalUnapproved = 'totalOwned (' + fN(vars.totalOwned) + ') - totalApproved (' + fN(vars.totalApproved) + ')';
		if (vars.cagr !== undefined) formulas.cagr = 'Compound annual growth rate from first event to now';
		if (vars.totalUnapprovedValue !== undefined) formulas.totalUnapprovedValue = 'Sum of (owned - approved) × (sharePrice - strikePrice) per class';
		if (vars.tvpi !== undefined) formulas.tvpi = '(totalValue + totalDistributions) / totalContributions';
		if (vars.dpi !== undefined) formulas.dpi = 'totalDistributions / totalContributions';
		if (vars.rvpi !== undefined) formulas.rvpi = 'totalValue / totalContributions';
		if (vars.moic !== undefined) formulas.moic = '(totalValue + totalDistributions) / totalContributions';
		if (vars.eligible !== undefined)
			formulas.eligible =
				typeof vars.eligible === 'number' && vars.stockStatus !== String(vars.eligible)
					? '= totalOwned (text status)'
					: 'Eligibility cap from stock status';
		if (vars.totalOriginalGrantValue !== undefined) formulas.totalOriginalGrantValue = 'Sum of "Original Grant Value" from unvested grants';
		if (vars.totalForecastedValue !== undefined) formulas.totalForecastedValue = 'Sum of "Forecasted Value" from unvested grants';
		if (vars.totalActualGrantValue !== undefined) formulas.totalActualGrantValue = 'Sum of "Actual Grant Value" from vested grants';

		// Resolve custom cap table columns and inject as template variables
		const client = legacyData.client || legacyData.clientData?.code || '';
		const userData = legacyData.userData || {};
		const clientData = legacyData.clientData || {};
		const dynamicTypes = {};
		this._resolveCustomColumns(vars, dynamicTypes, formulas, { userData, clientData, client });

		vars._formulas = formulas;
		vars._dynamicTypes = dynamicTypes;

		// Build context for path-based variable resolution
		// Allows templates to use {{user.idNumber}}, {{shares.eligible}}, etc.
		const userClientData = userData?.clients?.[client] || {};

		vars._context = {
			user: userClientData,
			shares: {}, // Portfolio-level doesn't have a specific class context
			config: clientData,
			aggregate: {},
		};

		// Per-owned-class variable maps for {{#sum}} blocks -- see processSumBlocks.
		if (needsClassVarMaps) {
			const ownedShares = userClientData.shares || {};
			vars._classVarMaps = shareKeys
				.filter((k) => Number(ownedShares[k]?.owned || 0) > 0)
				.map((k) => {
					const classData = calculatorResult?.breakdown?.find((b) => b.classKey === k);
					if (!classData) {
						console.warn('[HeadlineFormatter] {{#sum}}: no calculator breakdown for owned class, contributes 0:', k);
					}
					const equityConfig = clientData.equity?.[k] || {};
					return this.buildUnifiedClassVariableMap({
						classData,
						legacyData,
						classKey: k,
						equityName: equityConfig.name || k,
						currentLabel: equityConfig.currentLabel || '',
						aggregates: classAggregates?.[k],
					});
				});
		}

		return vars;
	},

	/**
	 * Build a unified variable map for a single class
	 * @param {Object} options
	 * @param {Object} options.classData - Class data from calculator breakdown (optional)
	 * @param {Object} options.legacyData - Legacy values from getEquityData()
	 * @param {string} options.classKey - The share class key
	 * @param {string} options.equityName - Display name for the class
	 * @param {string} options.currentLabel - Per-class headline label from equity config
	 * @returns {Object} - Flat map of variable names to values
	 */
	buildUnifiedClassVariableMap: function (options = {}) {
		const { classData, legacyData = {}, classKey, equityName, currentLabel, aggregates } = options;

		const {
			bases = {},
			latestValue = {},
			distribs = {},
			pendingDistribs = {},
			contributions = {},
			returnsOfCapital = {},
			shareKeys = [],
			itsaFund = false,
			currentClassValuationDate = '',
			valuationDate = '',
			totalPaidInCapital = 0,
			totalCapitalAccount = 0,
			totalDistributions = 0,
			totalPendingDistributions = 0,
		} = legacyData;

		// Calculate total cash (sum of CASH classes, used by ACR)
		let totalCash = 0;
		shareKeys.forEach(function (key) {
			if (key === 'CASH' || key.includes('CASH')) {
				totalCash += latestValue[key] || 0;
			}
		});

		// Helper to decode HTML entities in display strings
		const decodeHtml = (str) => {
			if (!str) return str;
			const txt = document.createElement('textarea');
			txt.innerHTML = str;
			return txt.value;
		};

		// Start with legacy values (always available)
		const vars = {
			// Class identifiers
			classKey: classKey || '',
			className: decodeHtml(equityName) || classKey || '',
			symbol: classKey || '',
			currentLabel: currentLabel || '', // Per-class headline label from equity config

			// Class values (with both prefixed and unprefixed names)
			value: latestValue[classKey] || 0,
			classValue: latestValue[classKey] || 0,
			totalValue: latestValue[classKey] || 0,
			classTotalValue: latestValue[classKey] || 0,

			// Cost basis
			basis: bases[classKey] || 0,
			classBasis: bases[classKey] || 0,

			// Contributions and distributions
			contributions: contributions[classKey] || 0,
			classContributions: contributions[classKey] || 0,
			distributions: distribs[classKey] || 0,
			classDistributions: distribs[classKey] || 0,
			pendingDistributions: pendingDistribs[classKey] || 0,
			classPendingDistributions: pendingDistribs[classKey] || 0,
			returnsOfCapital: returnsOfCapital[classKey] || 0,
			classReturnsOfCapital: returnsOfCapital[classKey] || 0,

			// Metadata
			accountType: itsaFund ? 'account' : 'shares',
			currentClassValuationDate: currentClassValuationDate,
			valuationDate: valuationDate,

			// Portfolio-level values (for class views that show aggregate totals)
			totalPaidInCapital: totalPaidInCapital,
			totalCapitalAccount: totalCapitalAccount,
			totalDistributions: totalDistributions,
			totalPendingDistributions: totalPendingDistributions,
			totalCash: totalCash,

			// Growth metrics (from calculator result, per-class if available)
			cagr: null,
		};

		// Overlay calculator class data if available (takes precedence)
		if (classData) {
			if (classData.value !== undefined) {
				vars.value = classData.value;
				vars.classValue = classData.value;
			}
			if (classData.totalValue !== undefined) {
				vars.totalValue = classData.totalValue;
				vars.classTotalValue = classData.totalValue;
			}
			if (classData.contributions !== undefined) {
				vars.contributions = classData.contributions;
				vars.classContributions = classData.contributions;
			}
			if (classData.distributions !== undefined) {
				vars.distributions = classData.distributions;
				vars.classDistributions = classData.distributions;
			}
			if (classData.capitalAccount !== undefined) vars.capitalAccount = classData.capitalAccount;
			if (classData.fundNav !== undefined) vars.fundNav = classData.fundNav;
			if (classData.userPercentage !== undefined) {
				vars.userPercentage = classData.userPercentage;
				vars.ownership = classData.userPercentage;
			}
			if (classData.tvpi !== undefined) {
				vars.tvpi = classData.tvpi;
				vars.classTvpi = classData.tvpi;
			}
			if (classData.cagr !== undefined) vars.cagr = classData.cagr;
			// Income allocations (tax basis) values
			if (classData.taxBasis !== undefined) {
				vars.taxBasis = classData.taxBasis;
				vars.classTaxBasis = classData.taxBasis;
				vars.totalTaxBasis = classData.taxBasis; // For class view, class value is the "total"
			}
			if (classData.incomeAllocations !== undefined) {
				vars.incomeAllocations = classData.incomeAllocations;
				vars.classIncomeAllocations = classData.incomeAllocations;
				vars.totalIncomeAllocations = classData.incomeAllocations;
			}
			// Market value from valuations
			if (classData.marketValue !== undefined) {
				vars.marketValue = classData.marketValue;
				vars.classMarketValue = classData.marketValue;
				vars.totalMarketValue = classData.marketValue; // For class view, class value is the "total"
			}
			if (classData.currentValue !== undefined && vars.marketValue === undefined) {
				// currentValue is an alias for marketValue
				vars.marketValue = classData.currentValue;
				vars.classMarketValue = classData.currentValue;
				vars.totalMarketValue = classData.currentValue;
			}
			// Unrealized gain/loss
			if (classData.unrealizedGain !== undefined) {
				vars.unrealizedGain = classData.unrealizedGain;
				vars.classUnrealizedGain = classData.unrealizedGain;
				vars.totalUnrealizedGain = classData.unrealizedGain;
			}
			// Private company vested/unvested values (BW-style)
			if (classData.vestedValue !== undefined) {
				vars.vestedValue = classData.vestedValue;
				vars.classVestedValue = classData.vestedValue;
			}
			if (classData.unvestedValue !== undefined) {
				vars.unvestedValue = classData.unvestedValue;
				vars.classUnvestedValue = classData.unvestedValue;
			}
			// Owned/Approved/Unapproved: raw values from userData for this class (LTIPR source of truth)
			if (legacyData.userData && legacyData.client && classKey) {
				const userShares = legacyData.userData.clients?.[legacyData.client]?.shares;
				const classOwned = Number(userShares?.[classKey]?.owned || 0);
				const classApproved = Number(userShares?.[classKey]?.approved || 0);
				const classUnapproved = Math.max(0, classOwned - classApproved);
				const perShareVal = (classData?.sharePrice || 0) - (classData?.strikePrice || 0);
				vars.owned = classOwned;
				vars.classOwned = classOwned;
				vars.ownedFraction = classOwned / 100;

				// ownershipDecimal: fractional ownership (e.g. 0.0025 for 0.25%)
				// Same priority as equity card: per-class totalShares → client metrics → userPercentage fallback
				const _clientData = legacyData.clientData;
				let _totalClassShares = resolveClassTotalShares(classKey, { clientData: _clientData, client: legacyData.client });
				if (!_totalClassShares && aggregates?.totalOwned !== undefined) {
					_totalClassShares = Number(aggregates.totalOwned);
				}
				// #6125: dollar-denominated LLC classes can legitimately run a negative
				// aggregate basis -- owned/_totalClassShares stays a valid fraction as
				// long as neither side is exactly 0 (share-count classes never go negative).
				if (_totalClassShares && classOwned) {
					vars.ownershipDecimal = classOwned / _totalClassShares;
				} else if (vars.userPercentage !== undefined) {
					vars.ownershipDecimal = vars.userPercentage;
				}
				vars.ownedValue = classOwned * perShareVal;
				vars.classOwnedValue = classOwned * perShareVal;
				vars.approved = classApproved;
				vars.classApproved = classApproved;
				vars.approvedValue = classApproved * perShareVal;
				vars.classApprovedValue = classApproved * perShareVal;
				vars.unapproved = classUnapproved;
				vars.classUnapproved = classUnapproved;
				vars.unapprovedValue = classUnapproved * perShareVal;
				vars.classUnapprovedValue = classUnapproved * perShareVal;
			}
			// Share price for display in subtitles
			if (classData.sharePrice !== undefined) {
				vars.sharePrice = classData.sharePrice;
				vars.classSharePrice = classData.sharePrice;
			}
			// Price change percentage from prior valuation
			if (classData.priceChange !== undefined && classData.priceChange !== null) {
				vars.priceChange = classData.priceChange;
				vars.classPriceChange = classData.priceChange;
			}
			// Absolute price change for display (always positive)
			if (classData.absPriceChange !== undefined && classData.absPriceChange !== null) {
				vars.absPriceChange = classData.absPriceChange;
				vars.classAbsPriceChange = classData.absPriceChange;
			}
		}

		// Build formula descriptions for each class variable (used by preview dropdown)
		const f$ = (v) => '$' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
		const fN = (v) => Number(v || 0).toLocaleString('en-US');
		const formulas = {};
		formulas.className = 'Equity config name';
		formulas.classKey = 'Share class identifier';
		formulas.symbol = '= classKey';
		formulas.currentLabel = 'Equity config currentLabel';
		formulas.value = classData?.value !== undefined ? 'shares × (sharePrice - strikePrice)' : 'Latest valuation value for ' + classKey;
		formulas.classValue = '= value';
		formulas.basis = 'Cost basis for ' + classKey;
		formulas.classBasis = '= basis';
		formulas.contributions = classData?.contributions !== undefined ? 'Total capital contributed to ' + classKey : 'contributions[' + classKey + ']';
		formulas.classContributions = '= contributions';
		formulas.distributions = classData?.distributions !== undefined ? 'Total distributions from ' + classKey : 'distribs[' + classKey + ']';
		formulas.classDistributions = '= distributions';
		formulas.pendingDistributions = 'Future-dated distributions from ' + classKey + ': pendingDistribs[' + classKey + ']';
		formulas.classPendingDistributions = '= pendingDistributions';
		formulas.returnsOfCapital = 'Returns of capital for ' + classKey;
		if (vars.vested !== undefined) formulas.vested = 'From schedule: shares vested per vesting schedule';
		if (vars.unvested !== undefined) formulas.unvested = 'From schedule: totalTarget (' + fN(vars.totalTarget) + ') - vested (' + fN(vars.vested) + ')';
		if (vars.totalTarget !== undefined) formulas.totalTarget = 'From schedule: total shares in vesting schedule';
		if (vars.vestedValue !== undefined) formulas.vestedValue = 'From schedule: vested (' + fN(vars.vested) + ') × (sharePrice - strikePrice)';
		if (vars.unvestedValue !== undefined) formulas.unvestedValue = 'From schedule: unvested (' + fN(vars.unvested) + ') × (sharePrice - strikePrice)';
		if (vars.sharePrice !== undefined) formulas.sharePrice = 'Current price per share from valuation';
		if (vars.tvpi !== undefined) formulas.tvpi = '(value + distributions) / contributions';
		if (vars.capitalAccount !== undefined) formulas.capitalAccount = 'contributions - distributions + returns for ' + classKey;
		if (vars.userPercentage !== undefined) formulas.userPercentage = 'User commitment / total fund commitments';
		if (vars.taxBasis !== undefined) formulas.taxBasis = 'contributions + income allocations';
		if (vars.marketValue !== undefined) formulas.marketValue = 'Latest valuation market value for ' + classKey;
		if (vars.unrealizedGain !== undefined) formulas.unrealizedGain = 'marketValue (' + f$(vars.marketValue) + ') - taxBasis (' + f$(vars.taxBasis) + ')';
		if (vars.priceChange !== undefined) formulas.priceChange = '% change from prior valuation price';
		if (vars.owned !== undefined) {
			formulas.owned = 'shares[' + classKey + '].owned';
			formulas.classOwned = '= owned';
			formulas.ownedFraction =
				'owned (' +
				fN(vars.owned) +
				') / 100 = ' +
				(vars.ownedFraction || 0) +
				' (only meaningful when owned is stored as a percentage, e.g. 25 = 25%)';
			if (vars.ownershipDecimal !== undefined) {
				const _fromFallback = vars.userPercentage !== undefined && vars.userPercentage === vars.ownershipDecimal;
				formulas.ownershipDecimal = (_fromFallback ? 'userPercentage (fallback)' : 'owned / totalClassShares') + ' = ' + vars.ownershipDecimal;
			}
			const sp = vars.sharePrice || 0;
			const stk = classData?.strikePrice || 0;
			formulas.ownedValue = fN(vars.owned) + ' × (' + f$(sp) + ' - ' + f$(stk) + ')';
			formulas.classOwnedValue = '= ownedValue';
			formulas.approved = 'shares[' + classKey + '].approved';
			formulas.classApproved = '= approved';
			formulas.approvedValue = fN(vars.approved) + ' × (' + f$(sp) + ' - ' + f$(stk) + ')';
			formulas.classApprovedValue = '= approvedValue';
			formulas.unapproved = 'owned (' + fN(vars.owned) + ') - approved (' + fN(vars.approved) + ')';
			formulas.classUnapproved = '= unapproved';
			formulas.unapprovedValue = 'unapproved (' + fN(vars.unapproved) + ') × (' + f$(sp) + ' - ' + f$(stk) + ')';
			formulas.classUnapprovedValue = '= unapprovedValue';
		}
		if (vars.classOriginalGrantValue !== undefined) formulas.classOriginalGrantValue = 'Sum of "Original Grant Value" from unvested grants';
		if (vars.classForecastedValue !== undefined) formulas.classForecastedValue = 'Sum of "Forecasted Value" from unvested grants';
		if (vars.classActualGrantValue !== undefined) formulas.classActualGrantValue = 'Sum of "Actual Grant Value" from vested grants';

		// Resolve custom cap table columns and inject as template variables
		const client = legacyData.client || legacyData.clientData?.code || '';
		const userData = legacyData.userData || {};
		const clientData = legacyData.clientData || {};
		const dynamicTypes = {};
		this._resolveCustomColumns(vars, dynamicTypes, formulas, { userData, clientData, client });

		vars._formulas = formulas;
		vars._dynamicTypes = dynamicTypes;

		// Build context for path-based variable resolution
		// Allows templates to use {{user.idNumber}}, {{shares.eligible}}, etc.
		const userClientData = userData?.clients?.[client] || {};
		const userShareData = userClientData?.shares?.[classKey] || {};
		const equityConfig = clientData?.equity?.[classKey] || {};

		// Compute per-class grant values from vesting schedule
		// Parses conditions strings to extract Original Grant Value and Forecasted Value
		const vesting = userShareData?.vesting;
		if (vesting && typeof vesting === 'object') {
			let classOriginalGrantValue = 0;
			let classForecastedValue = 0;
			let classActualGrantValue = 0;

			// Helper to parse money from string like "$29,987.25"
			const parseMoney = (str) => {
				if (!str || typeof str !== 'string') return null;
				const cleaned = str.replace(/[$,]/g, '');
				const num = parseFloat(cleaned);
				return isNaN(num) ? null : num;
			};

			Object.values(vesting).forEach((grant) => {
				if (!grant || typeof grant !== 'object') return;
				if (grant.cashedOut === true) return; // skip cashed-out tranches
				const conditions = grant.conditions;
				if (!conditions || typeof conditions !== 'string') return;

				// Parse Original Grant Value
				const originalMatch = conditions.match(/Original Grant Value:\s*(\$[\d,]+\.?\d*)/i);
				const originalValue = originalMatch ? parseMoney(originalMatch[1]) : null;

				// Parse Forecasted Value (unvested grants)
				const forecastMatch = conditions.match(/Forecasted Value:\s*(\$[\d,]+\.?\d*)/i);
				const forecastedValue = forecastMatch ? parseMoney(forecastMatch[1]) : null;

				// Parse Actual Grant Value (vested grants)
				const actualMatch = conditions.match(/Actual Grant Value:\s*(\$[\d,]+\.?\d*)/i);
				const actualValue = actualMatch ? parseMoney(actualMatch[1]) : null;

				// Accumulate based on vested status
				if (!grant.vested) {
					if (originalValue) classOriginalGrantValue += originalValue;
					if (forecastedValue) classForecastedValue += forecastedValue;
				} else {
					if (actualValue) classActualGrantValue += actualValue;
				}
			});

			// Add per-class grant values to vars
			if (classOriginalGrantValue > 0) {
				vars.classOriginalGrantValue = classOriginalGrantValue;
				vars.totalOriginalGrantValue = classOriginalGrantValue; // Alias for convenience
			}
			if (classForecastedValue > 0) {
				vars.classForecastedValue = classForecastedValue;
				vars.totalForecastedValue = classForecastedValue; // Alias for convenience
			}
			if (classActualGrantValue > 0) {
				vars.classActualGrantValue = classActualGrantValue;
				vars.totalActualGrantValue = classActualGrantValue; // Alias for convenience
			}
		}

		vars._context = {
			user: userClientData,
			shares: userShareData,
			config: equityConfig,
			aggregate: {},
		};

		return vars;
	},

	/**
	 * Format a template using unified variable map
	 * Processes conditionals first, then variable interpolation
	 *
	 * Supports both simple variables and path-based variables:
	 *   {{varName}}           - Simple variable from vars
	 *   {{user.idNumber}}     - Path-based: userData.clients[client].idNumber
	 *   {{shares.eligible}}   - Path-based: userData.clients[client].shares[class].eligible
	 *   {{config.totalShares}} - Path-based: clientData.equity[class].totalShares
	 *
	 * @param {string} template - Template with {{variable}} placeholders and conditionals
	 * @param {Object} vars - Variable map from buildUnifiedVariableMap (includes _context)
	 * @returns {string} - Interpolated string (HTML-safe)
	 */
	formatWithVars: function (template, vars) {
		if (!template) return '';

		const self = this;
		const context = vars._context || {};

		// Step 0: resolve {{#sum}} blocks first — see processSumBlocks.
		template = this.processSumBlocks(template, vars);

		// Create working copy to avoid mutating original vars
		const workingVars = Object.assign({}, vars);

		// Helper to get value - handles both simple vars and path-based vars
		const getValue = function (varExpr) {
			if (varExpr.includes('.') && /^(user|shares|config|aggregate)\./.test(varExpr)) {
				return self.resolvePathVariable(varExpr, context);
			}
			return TemplateUtils.safeGetVar(workingVars, varExpr);
		};

		// Helper to get type for formatting (checks per-call dynamic types before shared types)
		const dynamicTypes = vars._dynamicTypes || {};
		const getType = function (varExpr, formatOverride) {
			if (formatOverride) {
				if (formatOverride === 'dollars' || formatOverride === 'cents' || formatOverride === 'currency') return 'currency';
				if (formatOverride === 'number') return 'number';
				if (formatOverride === 'percentage') return 'percentage';
				if (formatOverride === 'raw') return 'raw';
			}
			if (!varExpr.includes('.')) {
				return dynamicTypes[varExpr] || self.variableTypes[varExpr] || 'raw';
			}
			return 'raw';
		};

		// Step 1: Pre-resolve path variables for conditionals
		const pathVarRegex = /\{\{#if\s+((?:user|shares|config|aggregate)\.[a-zA-Z_.]+)/g;
		let match;
		while ((match = pathVarRegex.exec(template)) !== null) {
			const pathVar = match[1];
			if (workingVars[pathVar] === undefined) {
				const value = self.resolvePathVariable(pathVar, context);
				if (value !== undefined) {
					workingVars[pathVar] = value;
				}
			}
		}

		// Step 2: Process conditionals ({{#if var}}...{{/if}}, {{var | default: "val"}})
		template = this.processConditionals(template, workingVars);

		// Step 3: Process format modifiers and filters {{varName | modifier}} or {{path.var | filter}}
		// Format modifiers: dollars, cents, currency, number, percentage, raw
		// Filters: isNumeric, toNumber, isText, uppercase, lowercase
		template = template.replace(/\{\{\s*([\w.]+)\s*\|\s*(\w+)\s*\}\}/g, function (_match, varExpr, modifier) {
			let value = getValue(varExpr);

			// Check if it's a filter first
			if (TemplateUtils.filters[modifier]) {
				// For 'round' filter, format with the variable's native type but rounded
				if (modifier === 'round') {
					const type = getType(varExpr);
					return self.formatValue(value, type, 'round');
				}
				value = TemplateUtils.applyFilter(value, modifier);
				// For toNumber filter, format as number
				if (modifier === 'toNumber' && typeof value === 'number') {
					return self.formatValue(value, 'number');
				}
				// For boolean filters like isNumeric/isText, just return the value
				return self.formatValue(value, 'raw');
			}

			// Otherwise treat as format modifier
			const type = getType(varExpr, modifier);
			return self.formatValue(value, type, modifier);
		});

		// Step 4: Process math expressions {{var1 + var2}}, {{var1 - var2 - var3}}, etc.
		template = this.processMathExpressions(template, workingVars);

		// Step 5: Process simple variable interpolation {{varName}} or {{path.var}}
		return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, function (_match, varExpr) {
			const value = getValue(varExpr);
			const type = getType(varExpr);
			return self.formatValue(value, type);
		});
	},

	/**
	 * Build headline using unified approach
	 * Works for any client - with or without calculator
	 * @param {Object} config - { template, subtitleTemplate, tertiaryTemplate } from clientData.language
	 * @param {Object} options - { calculatorResult, legacyData }
	 * @returns {Object} - { main: string, subtitle: string, tertiary: string }
	 */
	buildUnifiedHeadline: function (config, options = {}) {
		const template = this.getTemplate(config?.template, 'portfolio');
		const subtitleTemplate = this.getTemplate(config?.subtitleTemplate, 'portfolioSubtitle');
		const tertiaryTemplate = this.getTemplate(config?.tertiaryTemplate, 'portfolioTertiary');

		// _classVarMaps (per-owned-class variable maps, for {{#sum}} blocks) costs
		// a buildUnifiedClassVariableMap call per owned class -- skip building it
		// on the common path where none of this render's templates use {{#sum}}.
		const needsClassVarMaps = [template, subtitleTemplate, tertiaryTemplate].some((t) => t && t.includes('{{#sum}}'));
		const vars = this.buildUnifiedVariableMap({ ...options, needsClassVarMaps });

		return {
			main: this.formatWithVars(template, vars),
			subtitle: this.formatWithVars(subtitleTemplate, vars),
			tertiary: this.formatWithVars(tertiaryTemplate, vars),
		};
	},

	/**
	 * Resolve template syntax in clientData.about (or any free-form string)
	 * before it reaches a footnote helper. Backwards-compatible: strings without
	 * `{{...}}` are returned untouched (legacy `{24x_words}` placeholders pass
	 * through to whatever downstream code substitutes them). Added in #5198 Phase 7b
	 * so Cerity's footnote can consume {{value24x}} / {{cagr24x}} from the same
	 * template engine the headlines use.
	 *
	 * @param {string} aboutString - clientData.about or similar
	 * @param {Object} options - { calculatorResult, legacyData } same as buildUnifiedVariableMap
	 * @returns {string}
	 */
	renderAboutString: function (aboutString, options = {}) {
		if (!aboutString || typeof aboutString !== 'string') return aboutString || '';
		if (!aboutString.includes('{{')) return aboutString;
		const vars = this.buildUnifiedVariableMap(options);
		return this.formatWithVars(aboutString, vars);
	},

	/**
	 * Build class headline using unified approach
	 * Works for any client - with or without calculator
	 * @param {Object} config - { classTemplate, classSubtitleTemplate, classTertiaryTemplate } from clientData.language
	 * @param {Object} options - { classData, legacyData, classKey, equityName }
	 * @returns {Object} - { main: string, subtitle: string, tertiary: string }
	 */
	buildUnifiedClassHeadline: function (config, options = {}) {
		const template = this.getTemplate(config?.classTemplate || config?.template, 'class');
		const subtitleTemplate = this.getTemplate(config?.classSubtitleTemplate || config?.subtitleTemplate, 'classSubtitle');
		const tertiaryTemplate = this.getTemplate(config?.classTertiaryTemplate || config?.tertiaryTemplate, 'classTertiary');

		const vars = this.buildUnifiedClassVariableMap({ ...options, aggregates: options.aggregates });

		return {
			main: this.formatWithVars(template, vars),
			subtitle: this.formatWithVars(subtitleTemplate, vars),
			tertiary: this.formatWithVars(tertiaryTemplate, vars),
		};
	},
};

export { HeadlineFormatter };
